import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PhotoCamera from '@mui/icons-material/esm/PhotoCamera';
import documentService, { DocumentRegisterRow } from '../../services/api/documentService';

interface TaskPhotoEvidencePanelProps {
  projectId: string | null | undefined;
  taskIds: Array<string | null | undefined>;
  title?: string;
  dense?: boolean;
}

interface PhotoPreview {
  document: DocumentRegisterRow;
  url: string | null;
  error?: string;
}

export default function TaskPhotoEvidencePanel({
  projectId,
  taskIds,
  title = 'Task photos',
  dense = false,
}: TaskPhotoEvidencePanelProps) {
  const [photos, setPhotos] = useState<PhotoPreview[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const normalizedTaskIds = useMemo(
    () => Array.from(new Set(taskIds.filter((id): id is string => Boolean(id)))),
    [taskIds]
  );
  const taskKey = normalizedTaskIds.join('|');

  useEffect(() => {
    let cancelled = false;
    let objectUrls: string[] = [];
    setPhotos([]);
    setError(null);
    if (!projectId || normalizedTaskIds.length === 0) return undefined;

    setLoading(true);
    documentService.getRegister(projectId, { documentType: 'photo' })
      .then(async (register) => {
        const rows = register.documents.filter((document) => document.taskId && normalizedTaskIds.includes(document.taskId));
        const previews = await Promise.all(rows.map(async (document) => {
          try {
            const blob = await documentService.downloadFile(document.id);
            const url = URL.createObjectURL(blob);
            objectUrls.push(url);
            return { document, url };
          } catch (err: any) {
            return {
              document,
              url: null,
              error: err?.response?.data?.message || 'Photo could not be loaded.',
            };
          }
        }));
        if (!cancelled) setPhotos(previews);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || 'Task photos could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
      objectUrls = [];
    };
  }, [projectId, taskKey]);

  if (!projectId || normalizedTaskIds.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: dense ? 1.25 : 2, borderRadius: 1, bgcolor: '#fff' }}>
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PhotoCamera color="primary" fontSize={dense ? 'small' : 'medium'} />
          <Typography sx={{ fontWeight: 900 }}>{title}</Typography>
          {loading && <CircularProgress size={16} />}
        </Stack>
        {error && <Alert severity="warning">{error}</Alert>}
        {!loading && photos.length === 0 && !error && (
          <Typography variant="body2" color="text.secondary">No task photos have been uploaded yet.</Typography>
        )}
        {photos.length > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: dense ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
            {photos.map(({ document, url, error: photoError }) => (
              <Paper key={document.id} variant="outlined" sx={{ p: 1, borderRadius: 1, overflow: 'hidden' }}>
                {url ? (
                  <Box
                    component="img"
                    src={url}
                    alt={document.title}
                    sx={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', borderRadius: 0.75, display: 'block', bgcolor: 'grey.100' }}
                  />
                ) : (
                  <Alert severity="warning" sx={{ py: 0.5 }}>{photoError}</Alert>
                )}
                <Typography noWrap sx={{ mt: 0.75, fontWeight: 800 }}>{document.fileName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(document.createdAt).toLocaleString()}
                </Typography>
                {url && (
                  <Button size="small" href={url} target="_blank" rel="noreferrer" sx={{ mt: 0.5, px: 0 }}>
                    Open photo
                  </Button>
                )}
              </Paper>
            ))}
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
